import type { Component } from "svelte";
import type { Content } from "$lib/elev8Api/contentTypes";

import AlertContent from "$lib/content/components/AlertContent.svelte";
import NoticeContent from "$lib/content/components/NoticeContent.svelte";
import EmptyContent from "$lib/content/components/EmptyContent.svelte";
import HtmlContent from "$lib/content/components/HtmlContent.svelte";
import ImageContent from "$lib/content/components/ImageContent.svelte";
import LoadingContent from "./components/LoadingContent.svelte";
import RssContent from "$lib/content/components/RssFeedContent.svelte";
import VideoContent from "$lib/content/components/VideoContent.svelte";
import { reportError } from "$lib/errorHandling/errorReporter";
// import WeatherContent from "./components/WeatherContent.svelte";

// type ContentType = Content["content_type"] | "weather";
type ContentType = Content["content_type"];

const contentTypeToComponentMap: {
  [key in ContentType]: Component<any>;
} = {
  alert: AlertContent,
  notice: NoticeContent,
  empty: EmptyContent,
  html: HtmlContent,
  image: ImageContent,
  loading: LoadingContent,
  rss_feed: RssContent,
  video: VideoContent,
  // weather: WeatherContent,
};

function createContentComponentFactory() {
  return {
    getComponent(content: Content): Component<any> {
      const component = contentTypeToComponentMap[content.content_type];
      if (!component) {
        reportError(
          `[ContentComponentDataFactory] No component found for content type: ${content.content_type}`
        );
        return EmptyContent;
      }
      return component;
    },
    getSupportedContentTypes(): Content["content_type"][] {
      return Object.keys(
        contentTypeToComponentMap
      ) as Content["content_type"][];
    },
    isSupported(contentType: Content["content_type"]): boolean {
      return this.getSupportedContentTypes().includes(contentType);
    },
  };
}
const contentComponentFactory = createContentComponentFactory();

export { contentComponentFactory };

/* alt approaches:
  Dynamic component imports (especially if we get a lot of them)
  ```ts
    export async function getComponent(type: string) {
      switch (type) {
        case "button":
          return (await import("./components/Button.svelte")).default;
        case "card":
          return (await import("./components/Card.svelte")).default;
        // ...etc
        default:
          return null;
      }
    }
  ```
  
  Auto generated map using import.meta.glob
  ```ts
    const components = import.meta.glob("./components/*.svelte");
      const file = `./components/${capitalize(type)}.svelte`;
      if (modules[file]) {
        return (await modules[file]()).default;
      }
      return null;
    }
 */
